import React, { FC, useContext } from 'react';

import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { GICheckbox, GIFormControl, GIFormLabel } from '@components/common/app-components/GIInput';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { NUMBER } from '@constants/common/number';
import { RELATIONSHIP_TYPES } from '@constants/common/relationShipTypes';
import {
    LAST_DAY_OF_YEAR,
    TXN_REASON,
    TXN_TYPES,
    TXN_TYPES_OPTIONS,
} from '@constants/enrollmentList/enrollmentList.constant';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import Dropdown from '@lib/inputs/Dropdown';
import { changeFieldValue, setCustomError } from '../common/enrollmentUtils';
import { IEnrollee } from '../common/types';
import { AddOrChangeTxnFormContext } from '../context/AddOrChangeTxnProvider';
import { EnrollmentContext } from '../context/MemberMyEnrollmentProvider';

const StyledField = styled.div`
    width: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    .gi-checkbox__label {
        margin: 0;
    }
`;

interface EnrollmentTxnTypeRendererProps {
    enrollee: IEnrollee;
}

const EnrollmentTxnTypeRenderer: FC<EnrollmentTxnTypeRendererProps> = ({ enrollee }) => {
    const { isEdit } = useContext(EnrollmentContext);
    const { watch, setValue, clearErrors, setError } = useContext(AddOrChangeTxnFormContext);
    const values = watch();

    function updateEnrolleeEnddateCheck(enrollee) {
        var isTermDateError = false;
        values.enrolleeDataDtoList.map(function (householdEnrollee) {
            if (
                householdEnrollee.relationshipToSubscriber.toUpperCase() !== RELATIONSHIP_TYPES.SELF.value &&
                householdEnrollee.memberId !== enrollee.memberId &&
                householdEnrollee.txnType === TXN_TYPES.Term &&
                householdEnrollee.enrolleeEffectiveEndDateUI.substring(NUMBER.ZERO, NUMBER.FIVE) === LAST_DAY_OF_YEAR
            ) {
                isTermDateError = true;
            }
        });

        if (isTermDateError) {
            setCustomError(setError, 'enrolleeEndDateUnchanged', 'error.enrollment.termEnrollee.endDateError');
        }
    }

    const handleChange = value => {
        if (value == TXN_TYPES.Add) {
            enrollee.txnReason = TXN_REASON.EC;
        } else if (value == TXN_TYPES.Change) {
            enrollee.txnReason = TXN_REASON.TwentyNine;
        } else {
            enrollee.txnReason = TXN_REASON.AI;
        }

        if (value == TXN_TYPES.Term) {
            if (enrollee.enrolleeEffectiveEndDateUI.substring(NUMBER.ZERO, NUMBER.FIVE) === LAST_DAY_OF_YEAR) {
                setCustomError(setError, 'enrolleeEndDateUnchanged', 'error.enrollment.termEnrollee.endDateError');
            } else {
                clearErrors('enrolleeEndDateUnchanged');
            }
        } else {
            clearErrors('enrolleeEndDateUnchanged');
            updateEnrolleeEnddateCheck(enrollee);
        }

        changeFieldValue(setValue, `enrolleeDataDtoList.${enrollee.index}.txnType`, value);
    };

    const handleCheckBoxClick = event => {
        changeFieldValue(setValue, `enrolleeDataDtoList.${enrollee.index}.txnType`, event.target.checked);
    };

    return (
        <>
            <ConditionalRendering isTrue={!isEdit}>
                <StyledField>
                    <GIFormControl
                        id={`txnType-form-${enrollee.firstName}`}
                        name={`txnType-${enrollee.firstName}`}
                        type="checkbox"
                    >
                        <GICheckbox
                            id={`txnType-${enrollee.firstName}`}
                            name={`txnType-${enrollee.firstName}`}
                            value={enrollee.txnType}
                            checked={!!enrollee.txnType}
                            disabled={enrollee.relationshipToSubscriber.toUpperCase() === RELATIONSHIP_TYPES.SELF.value}
                            onChange={handleCheckBoxClick}
                        />
                        <GIFormLabel id={`txnType-${enrollee.firstName}`} name={`txnType-${enrollee.firstName}`}>
                            <FormattedMessage id="common.no_text" />
                        </GIFormLabel>
                    </GIFormControl>
                </StyledField>
            </ConditionalRendering>
            <ConditionalRendering isTrue={isEdit}>
                <Dropdown
                    label={{
                        text: '',
                        labelClass: [''],
                    }}
                    inputData={{
                        inputClasses: [''],
                        options: TXN_TYPES_OPTIONS,
                        type: INPUT_TYPES.DROPDOWN,
                        value: enrollee.txnType,
                        isInputRequired: false,
                        disabled: enrollee.relationshipToSubscriber.toUpperCase() === RELATIONSHIP_TYPES.SELF.value,
                    }}
                    inputActions={{
                        handleFieldChange: handleChange,
                    }}
                />
            </ConditionalRendering>
        </>
    );
};

export default EnrollmentTxnTypeRenderer;
